import { useNavigate } from "react-router-dom";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  UserPlus, PaperPlaneTilt, ChatCircleDots, Buildings, UserCircle, Crown,
  Clock, ArrowSquareOut,
} from "@phosphor-icons/react";

// Système de statut commercial (CRM/GTM). Précédence : replied > contacted > selected.
const STATES = {
  selected: {
    label: "Consultant sélectionné", icon: UserPlus, color: "#8A8A93",
    tip: "Consultant positionné — aucun email envoyé pour le moment.",
  },
  contacted: {
    label: "Contacté", icon: PaperPlaneTilt, color: "#F97316",
    tip: "Entreprise contactée — Consultant positionné",
  },
  replied: {
    label: "Réponse reçue", icon: ChatCircleDots, color: "#22C55E",
    tip: "Le décideur a répondu à votre email.",
  },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};

export default function CommercialStatusBadge({ positioning, opportunityId, companyName, contactTitle }) {
  const navigate = useNavigate();
  if (!positioning || !positioning.state) return null;

  const meta = STATES[positioning.state] || STATES.selected;
  const Icon = meta.icon;
  const name = positioning.consultant_name;
  const extra = positioning.count > 1 ? positioning.count - 1 : 0;
  const c = meta.color;

  const tooltip = positioning.state === "contacted" && name
    ? `Entreprise contactée — Consultant positionné : ${name}`
    : meta.tip;

  const openEmail = (e) => {
    e.stopPropagation();
    if (opportunityId) navigate(`/app/opportunity/${opportunityId}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title={tooltip}
          data-testid={`commercial-status-${positioning.state}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-transform hover:scale-[1.03] cursor-pointer"
          style={{ color: c, borderColor: c + "66", background: c + "1f" }}
        >
          <Icon size={13} weight="fill" />
          <span>{meta.label}</span>
          {name && <span className="opacity-90">· {name}</span>}
          {extra > 0 && <span className="opacity-75">+{extra}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 bg-surface border-border p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="commercial-status-popover"
      >
        <div className="px-4 py-3 border-b border-border flex items-center gap-2" style={{ background: c + "12" }}>
          <Icon size={16} weight="fill" style={{ color: c }} />
          <span className="text-sm font-display font-bold text-fg">{meta.label}</span>
        </div>
        <div className="p-4 space-y-2.5 text-xs">
          <div className="overline mb-0.5">Activité commerciale</div>
          {positioning.state !== "selected" && (
            <Row icon={Buildings} label="Entreprise contactée" value={companyName || "—"} />
          )}
          {name && (
            <Row icon={UserCircle} label={extra > 0 ? "Consultants positionnés" : "Consultant positionné"}
              value={extra > 0 ? (positioning.consultants || [name]).join(", ") : name} />
          )}
          {positioning.contact_name && (
            <Row icon={Crown} label="Contact" value={positioning.contact_name} sub={contactTitle} />
          )}
          {positioning.last_reply_at && (
            <Row icon={ChatCircleDots} label="Réponse reçue le" value={fmtDate(positioning.last_reply_at)} accent="#22C55E" />
          )}
          {positioning.last_contacted_at && (
            <Row icon={Clock} label="Email envoyé le" value={fmtDate(positioning.last_contacted_at)} />
          )}
          {positioning.sender_name && (
            <Row icon={PaperPlaneTilt} label="Envoyé par" value={positioning.sender_name} />
          )}
          {positioning.state === "selected" && (
            <p className="text-muted leading-relaxed pt-1">Aucun email n'a encore été envoyé pour cette opportunité.</p>
          )}
        </div>
        {opportunityId && positioning.state !== "selected" && (
          <button onClick={openEmail} data-testid="commercial-status-view-email"
            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-accent border-t border-border px-4 py-2.5 hover:bg-surface-2 transition-colors">
            Voir l'email <ArrowSquareOut size={13} />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Row({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-muted shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-muted">{label}</div>
        <div className="text-fg font-medium truncate" style={accent ? { color: accent } : undefined}>{value || "—"}</div>
        {sub && <div className="text-muted truncate">{sub}</div>}
      </div>
    </div>
  );
}
